import { useState, useCallback, useEffect, memo } from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useAppSelector } from 'app';
import AppSnackbar from 'components/snackbar/snackbar';
import config from 'config';
import DepartmentAPI from 'components/setting/api/department-api';
import type { LinkFacebookAccountRequest } from 'components/setting/api/department-api';
import { useFacebookTokenContext } from 'common/context/FacebookTokenContext';
import { useSocialTestDealer } from 'common/context/SocialTestDealerContext';
import { useFacebookSDK } from 'common/hooks';
import { isDMSLogin } from 'common/utils/shared/login-utils';
import type { FacebookCallbackResponse } from 'common/models/facebook';
import { FB_OAUTH_STATE_KEY, FB_OAUTH_RETURN_PATH_KEY } from './facebook-oauth-constants';

/**
 * Facebook Login Scopes
 * 
 * Basic & Page Permissions:
 * - public_profile, email: Basic user info
 * - pages_show_list: List Facebook Pages user manages
 * - pages_messaging: Send/receive messages on Facebook Pages
 * - pages_manage_metadata: Subscribe to page webhooks
 * 
 * Instagram Permissions:
 * - instagram_basic: Access Instagram account info
 * - instagram_manage_messages: Send/receive Instagram DMs
 * 
 * Business Management:
 * - business_management: Manage business portfolios, link assets (required for FB-to-Instagram linking)
 * 
 * Ads Permissions (for Sponsored Messages after 7 days):
 * - ads_management: Create and manage ad campaigns
 * - ads_read: Read ad account data
 */
const FACEBOOK_LOGIN_SCOPES = [
  // Basic
  'public_profile',
  'email',
  // Page Management
  'pages_show_list',
  'pages_messaging',
  'pages_manage_metadata',
  // Instagram
  'instagram_basic',
  'instagram_manage_messages',
  // Business Management (required for linking FB Page to Instagram via Business Portfolio)
  'business_management',
].join(',');

interface FacebookLoginButtonProps {
  isAccountConnected?: boolean;
  setIsAccountConnected: (value: boolean) => void;
  locationId: number | undefined;
  onRefresh: () => void;
  parentLoading?: boolean;
}

const FacebookLoginButton: React.FC<FacebookLoginButtonProps> = memo(({ 
  onRefresh, 
  isAccountConnected, 
  setIsAccountConnected, 
  locationId, 
  parentLoading 
}) => {
  const [showSnackbar] = AppSnackbar.useSnackBar();
  const dealerId = useAppSelector((state) => state.user.dealerId);
  const crmUserId = useAppSelector((state) => state.user.crmUserId);
  const facebookAppId = config.get('api.facebookAppId');
  const [loading, setLoading] = useState<boolean>(false);
  const { isFacebookTestDealer, isCheckingSocialTestDealer } = useSocialTestDealer();
  
  const { incrementTokenVersion } = useFacebookTokenContext();

  // Initialize Facebook SDK
  const { isSdkLoaded, isLoading: isSdkLoading, error: sdkError, login } = useFacebookSDK({
    appId: facebookAppId,
    autoLoad: true,
  });

  // Show error if SDK fails to load
  useEffect(() => {
    if (sdkError) {
      showSnackbar({
        type: "error",
        message: "Failed to load Facebook SDK. Please refresh the page and try again.",
      });
    }
  }, [sdkError, showSnackbar]);

  const loadingState = loading || parentLoading || isCheckingSocialTestDealer || isSdkLoading;

  const responseFacebook = useCallback(async (response: FacebookCallbackResponse) => {
    // Facebook Login Status Types:
    // 'connected' - User is logged into Facebook AND has authorized your app
    // 'not_authorized' - User is logged into Facebook but has NOT authorized your app
    // 'unknown' - User is not logged into Facebook OR cancelled the popup
    // undefined/empty - User closed the popup without completing login

    // Early exit if no response or user cancelled the popup
    if (!response || Object.keys(response).length === 0) {
      setLoading(false);
      return; // User closed popup - silent exit
    }

    // Handle explicit status responses
    if (response.status === 'unknown') {
      setLoading(false);
      return; // User cancelled or not logged in - silent exit
    }

    if (response.status === 'not_authorized') {
      setLoading(false);
      showSnackbar({
        type: "warning",
        message: "Please authorize the app to continue with Facebook login.",
      });
      return;
    }

    // At this point, status should be 'connected' - validate we have required data
    if (!response.accessToken) {
      setLoading(false);
      showSnackbar({
        type: "error",
        message: "Failed to get Facebook access token. Please try again.",
      });
      return;
    }

    if (!response.userID) {
      setLoading(false);
      showSnackbar({
        type: "error",
        message: "Unable to retrieve Facebook user information. Please try again.",
      });
      return;
    }

    try {

      const linkFbAccountRequest: LinkFacebookAccountRequest = {
        dealerId: dealerId,
        locationId: locationId ?? 0,
        userAccessToken: response.accessToken,
        facebookUserId: Number(response.userID),
        facebookEmail: response.email ?? '',
        facebookUserName: response.name ?? '',
        crmUserId: crmUserId
      };
      
      await DepartmentAPI.linkFacebookAccount(linkFbAccountRequest);
      showSnackbar({ type: 'success', message: 'Facebook Account linked successfully.' });
      setIsAccountConnected(true);
      incrementTokenVersion(); // Notify context consumers (like TokenExpiredBanner)
      onRefresh();
      
    } catch (error) {
      let errorMsg = 'An error occurred while linking Facebook account.';
      if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMsg = (error as { message: string }).message;
      }
      showSnackbar({ type: 'error', message: errorMsg });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, dealerId, crmUserId]);

  // Hide button if dealer is not in the test dealer list
  if (!isCheckingSocialTestDealer && !isFacebookTestDealer) {
    return null;
  }

  const handleFacebookLogin = () => {
    if (!locationId || locationId === 0) {
      showSnackbar({
        type: "warning",
        message: "Please select a valid location before linking Facebook account.",
      });
      return;
    }

    // DMS WebView: use redirect-based OAuth (popups don't work in WebView2)
    if (isDMSLogin()) {
      setLoading(true);
      const oauthState = JSON.stringify({ dealerId, locationId, crmUserId });
      const returnPath = window.location.pathname + window.location.hash;
      sessionStorage.setItem(FB_OAUTH_STATE_KEY, oauthState);
      sessionStorage.setItem(FB_OAUTH_RETURN_PATH_KEY, returnPath);

      const redirectUri = `${window.location.origin}/facebook-callback`;
      const oauthUrl = `https://www.facebook.com/v21.0/dialog/oauth?` +
        `client_id=${facebookAppId}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&scope=${FACEBOOK_LOGIN_SCOPES}` +
        `&response_type=code`;

      window.location.href = oauthUrl;
      return;
    }

    // Browser: use normal popup-based flow
    if (!isSdkLoaded) {
      showSnackbar({
        type: "error",
        message: "Facebook SDK is not ready. Please try again.",
      });
      return;
    }

    setLoading(true);
    
    // Call FB.login() directly - popup opens synchronously from user click
    login(
      { scope: FACEBOOK_LOGIN_SCOPES, fields: 'name,email,picture' },
      responseFacebook
    );
  };

  return (
    <Button
      variant="outlined"
      color="primary"
      disabled={loadingState || !isSdkLoaded}
      onClick={handleFacebookLogin}
      endIcon={isAccountConnected ? <CheckCircleIcon color="success" /> : undefined}
    >
      {loadingState ? <CircularProgress size={20} color="inherit" /> : 'Link Facebook'}
    </Button>
  );
});

FacebookLoginButton.displayName = 'FacebookLoginButton';

export default FacebookLoginButton;
